import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownLeft,
  ArrowDownToLine,
  ArrowUpRight,
  Banknote,
  Check,
  Clock,
  ExternalLink,
  Info,
  Landmark,
  Loader2,
  Lock,
  Plus,
  ShieldCheck,
  Smartphone,
  TriangleAlert,
  Wallet as WalletIcon,
} from "lucide-react";
import type { FundingOrder, StatementLine } from "@evocha/shared-types";
import { api } from "../lib/api";
import {
  errorMessage,
  relativeTime,
  useFundingOrders,
  useStatement,
  useSummary,
  useWallet,
} from "../lib/queries";
import { useSession } from "../lib/session";
import { tzs } from "../lib/format";

const PRESETS = [10000, 25000, 50000, 100000, 250000, 500000];

const TYPE_LABEL: Record<StatementLine["type"], string> = {
  funding: "Wallet top-up",
  reserve: "Airtime sale held",
  capture: "Airtime sale settled",
  release: "Reservation returned",
  commission: "Commission credit",
  refund: "Refund",
  reversal: "Reversal",
  adjustment: "Adjustment",
};

const FUNDING_LABEL: Record<FundingOrder["status"], string> = {
  awaiting_payment: "Awaiting payment",
  pending: "Confirming payment",
  credited: "Credited to wallet",
  failed: "Payment failed",
  expired: "Checkout expired",
};

export function Wallet() {
  const queryClient = useQueryClient();
  const { agent } = useSession();
  const wallet = useWallet();
  const statement = useStatement({ limit: 25 });
  const fundingOrders = useFundingOrders(10);
  const today = useSummary("daily");

  const [amount, setAmount] = useState<number | null>(50000);
  const [custom, setCustom] = useState("");
  const [funding, setFunding] = useState<FundingOrder | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ledgerFilter, setLedgerFilter] = useState<"all" | "in" | "out">("all");

  const chosen = custom !== "" ? Number(custom.replace(/\D/g, "")) || 0 : amount ?? 0;
  const balances = wallet.data;
  const lines = statement.data?.lines ?? [];

  const rows = useMemo(() => {
    if (ledgerFilter === "all") return lines;
    return lines.filter((line) => (ledgerFilter === "in" ? Number(line.credit_minor) > 0 : Number(line.debit_minor) > 0));
  }, [lines, ledgerFilter]);

  const dailySold = Number(today.data?.sales.gross_minor ?? 0);
  const dailyCount = today.data?.sales.count ?? 0;
  const dailyLimit = Number(agent?.limit_daily_minor ?? 0);
  const txnLimit = Number(agent?.limit_txn_minor ?? 0);

  async function startTopUp() {
    if (chosen < 1000 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createFunding(chosen * 100);
      setFunding(created);
      if (created.checkout_url) window.open(created.checkout_url, "_blank", "noopener,noreferrer");
      await queryClient.invalidateQueries({ queryKey: ["funding-orders"] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // Poll Selcom while a checkout is open; the server reconciles on each read.
  useEffect(() => {
    if (!funding) return;
    if (funding.status !== "pending" && funding.status !== "awaiting_payment") return;
    const timer = window.setInterval(async () => {
      try {
        const next = await api.fundingOrder(funding.id);
        setFunding(next);
        if (next.status === "credited") {
          void queryClient.invalidateQueries({ queryKey: ["wallet"] });
          void queryClient.invalidateQueries({ queryKey: ["statement"] });
          void queryClient.invalidateQueries({ queryKey: ["funding-orders"] });
        }
      } catch {
        /* keep polling; transient network errors are expected */
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [funding?.id, funding?.status, queryClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const fundingActive = funding && (funding.status === "pending" || funding.status === "awaiting_payment");

  return (
    <div className="stack-6">
      <div className="page-head">
        <div>
          <h2 className="ev-h2">Wallet</h2>
          <p className="ev-muted">One TZS balance. Funds are reserved before every network call.</p>
        </div>
        <div className="page-head-actions">
          <Link to="/statements" className="ev-btn ev-btn-sm ev-btn-secondary"><ArrowDownToLine size={15} /> Statements</Link>
          <button className="ev-btn ev-btn-sm ev-btn-primary" onClick={() => document.getElementById("topup-card")?.scrollIntoView({ behavior: "smooth", block: "center" })}>
            <Plus size={15} /> Top up
          </button>
        </div>
      </div>

      <div className="wallet-layout">
        <div className="stack-5">
          <section className="wallet-hero">
            <div className="wallet-hero-top">
              <span className="wallet-hero-label"><WalletIcon size={16} /> Available balance</span>
              <span className="wallet-hero-badge">
                {wallet.isLoading ? <Loader2 size={12} className="spin" /> : <span className="ev-dot" />}
                {wallet.isLoading ? " Loading" : " Active"}
              </span>
            </div>
            <div className="wallet-hero-amount">{wallet.isLoading ? "…" : tzs(balances?.available_minor ?? 0)}</div>
            <p className="wallet-hero-sub">Agent wallet · TZS · {agent?.code ?? "—"}</p>
            <div className="wallet-hero-grid">
              <div><span>Reserved</span><strong>{tzs(balances?.reserved_minor ?? 0)}</strong></div>
              <div><span>Commission bucket</span><strong>{tzs(balances?.commission_minor ?? 0)}</strong></div>
              <div><span>Daily limit left</span><strong>{tzs(Math.max(dailyLimit - dailySold, 0))}</strong></div>
            </div>
            <div className="wallet-hero-foot">
              <ShieldCheck size={15} /> Held with evocha · operated by SKT Tanzania Limited
            </div>
          </section>

          {!!balances?.blocks?.length && (
            <div className="info-card" role="status">
              <Lock size={16} />
              <p>
                {balances.blocks.length} amount{balances.blocks.length === 1 ? " is" : "s are"} on hold:{" "}
                {balances.blocks.map((b) => `${tzs(b.amount_minor)} (${b.reason})`).join(", ")}.
              </p>
            </div>
          )}

          <section className="ev-card">
            <div className="ev-card-head">
              <div>
                <h3 className="ev-card-title">Wallet activity</h3>
                <p className="ev-caption">Immutable double-entry ledger — every row balances.</p>
              </div>
              <div className="segmented">
                {(["all", "in", "out"] as const).map((f) => (
                  <button key={f} className={ledgerFilter === f ? "segmented-on" : ""} onClick={() => setLedgerFilter(f)}>
                    {f === "all" ? "All" : f === "in" ? "Money in" : "Money out"}
                  </button>
                ))}
              </div>
            </div>
            <div className="ev-table-wrap">
              <table className="ev-table wallet-ledger-table">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Detail</th>
                    <th className="ev-num">Amount</th>
                    <th className="ev-num">Balance</th>
                    <th className="ev-num">When</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.isLoading && (
                    <tr><td colSpan={5} className="ev-muted" style={{ textAlign: "center", padding: 24 }}><Loader2 size={18} className="spin" /></td></tr>
                  )}
                  {statement.isError && (
                    <tr><td colSpan={5} className="ev-muted" style={{ textAlign: "center", padding: 24 }}>{errorMessage(statement.error)}</td></tr>
                  )}
                  {!statement.isLoading && !statement.isError && rows.length === 0 && (
                    <tr><td colSpan={5} className="ev-muted" style={{ textAlign: "center", padding: 24 }}>No ledger entries yet. Fund your wallet to start selling.</td></tr>
                  )}
                  {rows.map((r) => {
                    const incoming = Number(r.credit_minor) > 0;
                    const amount = incoming ? r.credit_minor : r.debit_minor;
                    return (
                      <tr key={r.journal_ref}>
                        <td>
                          <span className={`ledger-icon ledger-${incoming ? "positive" : "negative"}`}>
                            {incoming ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                          </span>
                          <span className="ledger-label">{TYPE_LABEL[r.type] ?? r.type}</span>
                        </td>
                        <td className="ev-muted">{r.description || r.order_ref || "—"}</td>
                        <td className={`ev-num ${incoming ? "ev-pos" : "ev-money"}`}>
                          {incoming ? "+" : "−"}{tzs(amount)}
                        </td>
                        <td className="ev-num ev-muted">{tzs(r.balance_minor)}</td>
                        <td className="ev-num ev-muted" title={new Date(r.occurred_at).toLocaleString("en-GB")}>{relativeTime(r.occurred_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="tx-foot">
              <span className="ev-caption">Entries are never edited or deleted. Corrections are new entries.</span>
              <Link to="/statements" className="ev-btn ev-btn-sm ev-btn-ghost">Open statements <ArrowUpRight size={14} /></Link>
            </div>
          </section>
        </div>

        <aside className="stack-5">
          <section className="ev-card ev-card-pad topup-card" id="topup-card">
            <div className="ev-row-between">
              <div>
                <span className="ev-eyebrow">Top up</span>
                <h3 className="ev-h3" style={{ marginTop: 4 }}>Add funds</h3>
              </div>
              <span className="topup-instant"><Lock size={12} /> Selcom</span>
            </div>

            <div className="topup-amount">
              <span className="ev-label">Amount</span>
              <div className="ev-input-affix">
                <span className="ev-affix ev-affix-left">TZS</span>
                <input
                  className="ev-input input-lg"
                  inputMode="numeric"
                  value={custom !== "" ? custom : amount ? String(amount) : ""}
                  onChange={(e) => { setCustom(e.target.value.replace(/\D/g, "")); setAmount(null); }}
                  placeholder="0"
                  aria-label="Top-up amount"
                />
              </div>
            </div>

            <div className="topup-presets">
              {PRESETS.map((value) => (
                <button key={value} className={custom === "" && amount === value ? "topup-chip topup-chip-on" : "topup-chip"}
                  onClick={() => { setAmount(value); setCustom(""); }}>
                  {new Intl.NumberFormat("en-TZ").format(value)}
                </button>
              ))}
            </div>

            <div className="ev-field" style={{ marginTop: 18 }}>
              <span className="ev-label">Payment</span>
              <div className="provider provider-on" style={{ cursor: "default" }}>
                <span className="provider-mark" style={{ background: "#0057B814", color: "#0057B8" }}>
                  <Smartphone size={17} />
                </span>
                <span className="provider-text">
                  <strong>Selcom checkout</strong>
                  <span>M-Pesa, Mixx by Yas, Airtel Money or card</span>
                </span>
                <span className="radio radio-on" />
              </div>
            </div>

            <button className="ev-btn ev-btn-gradient ev-btn-block ev-btn-lg" style={{ marginTop: 18 }} onClick={() => void startTopUp()} disabled={chosen < 1000 || busy}>
              {busy ? <><Loader2 size={16} className="spin" /> Starting checkout…</> : <>Top up {chosen >= 1000 ? tzs(chosen * 100) : ""}</>}
            </button>
            <p className="ev-hint ev-center" style={{ marginTop: 10 }}>
              Minimum TZS 1,000 · You approve the payment on your phone
            </p>

            {error && (
              <div className="inline-success" role="alert" style={{ background: "var(--ev-red-100)", color: "var(--ev-red-600)" }}>
                <TriangleAlert size={15} />
                <span>{error}</span>
              </div>
            )}

            {funding && (
              <div className="inline-success" role="status" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {funding.status === "credited" ? <Check size={15} /> : fundingActive ? <Loader2 size={15} className="spin" /> : <Clock size={15} />}
                  <strong>{FUNDING_LABEL[funding.status]}</strong>
                </span>
                <span>{tzs(funding.amount_minor)} · {funding.payment_reference}</span>
                {fundingActive && funding.checkout_url && (
                  <a className="ev-btn ev-btn-sm ev-btn-secondary" href={funding.checkout_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={14} /> Open payment page
                  </a>
                )}
              </div>
            )}
          </section>

          <section className="ev-card ev-card-pad">
            <span className="ev-eyebrow">Recent top-ups</span>
            <div className="security-list" style={{ marginTop: 10 }}>
              {fundingOrders.isLoading && <div className="ev-caption"><Loader2 size={14} className="spin" /> Loading…</div>}
              {!fundingOrders.isLoading && (fundingOrders.data?.items.length ?? 0) === 0 && (
                <div className="ev-caption">No top-ups yet.</div>
              )}
              {fundingOrders.data?.items.map((f) => (
                <div className="security-row" key={f.id}>
                  <span className="doc-icon"><Banknote size={17} /></span>
                  <div>
                    <strong>{tzs(f.amount_minor)}</strong>
                    <span>{FUNDING_LABEL[f.status]} · {relativeTime(f.created_at)}</span>
                  </div>
                  <span className={`ev-badge ${f.status === "credited" ? "ev-badge-success" : f.status === "failed" || f.status === "expired" ? "ev-badge-danger" : "ev-badge-pending"}`}>
                    {f.status === "credited" ? "Credited" : f.status}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="ev-card ev-card-pad">
            <span className="ev-eyebrow">Settlement account</span>
            <div className="bank-row">
              <span className="bank-mark"><Landmark size={18} /></span>
              <div>
                <strong>Bank settlement</strong>
                <span>Managed by evocha operations</span>
              </div>
              <span className="ev-badge ev-badge-neutral">Ops</span>
            </div>
            <Link to="/support" className="ev-btn ev-btn-sm ev-btn-secondary ev-btn-block" style={{ marginTop: 14 }}>Request settlement</Link>
          </section>

          <div className="info-card">
            <Info size={16} />
            <p>
              A sale reserves its amount before we call the network. If the network does not confirm, the reservation stays
              until we resolve it — your balance can never go negative.
            </p>
          </div>
        </aside>
      </div>

      <section className="ev-card ev-card-pad wallet-limits">
        <div>
          <span className="ev-eyebrow">Today’s limits</span>
          <h3 className="ev-h3" style={{ marginTop: 4 }}>You have room to sell</h3>
        </div>
        <div className="wallet-limit-grid">
          <div>
            <div className="ev-row-between"><span className="ev-caption">Daily volume</span><strong className="ev-caption ev-ink">{tzs(dailySold)} / {tzs(dailyLimit)}</strong></div>
            <div className="limit-track"><span style={{ width: `${dailyLimit ? Math.min((dailySold / dailyLimit) * 100, 100) : 0}%` }} /></div>
          </div>
          <div>
            <div className="ev-row-between"><span className="ev-caption">Single sale</span><strong className="ev-caption ev-ink">{tzs(txnLimit)} max</strong></div>
            <div className="limit-track"><span style={{ width: "100%" }} /></div>
          </div>
          <div>
            <div className="ev-row-between"><span className="ev-caption">Today’s sales</span><strong className="ev-caption ev-ink">{dailyCount}</strong></div>
            <div className="limit-track"><span style={{ width: `${Math.min(dailyCount, 100)}%` }} /></div>
          </div>
        </div>
        <Link to="/support" className="ev-btn ev-btn-sm ev-btn-secondary">Request a limit increase</Link>
      </section>
    </div>
  );
}
